import { Box, Grid } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisHeader from '../AnalysisHeader'
import BaseSource from '../../Commons/Form/BaseSource'
import ColumnsDropdown from '../../Commons/Form/ColumnsDropdown'
import { ColumnsFromSourceSettings } from './types'
import MultipleColumnsDropdown from '../../Commons/Form/MultipleColumnsDropdown'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import { generateColumnsSourceQuery } from './queries'
import SecondSourceSelector from '../../Commons/AnalysisSource/Form/SourceSelector'
import SecondSourceColumnSelector from '../../Commons/AnalysisSource/Form/SourceColumnSelector'
import { AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'

function ColumnsFromSourceForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: ColumnsFromSourceSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()

  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const [sourceColumn, setSourceColumn] = useState<string>(settings?.mainSourceColumn || '')

  const secondSourceTable = useAppSelector((state) => state.analysis.secondSourceTable)
  const [secondSource, setSecondSource] = useState<string>(settings?.secondSource || secondSourceTable?.id || '')
  const secondSourceDataset =
    useAppSelector((state) => getEnrichedDatasetById(state, secondSource as string)) || secondSourceTable
  const [secondSourceColumn, setSecondSourceColumn] = useState<string>(settings?.secondSourceColumn || '')

  const [newTableSourceColumns, setNewTableSourceColumns] = useState<Record<string, string>[]>(
    settings?.newTableSourceColumns || []
  )
  const [newTableSecondSourceColumns, setNewTableSecondSourceColumns] = useState<Record<string, string>[]>(
    settings?.newTableSecondSourceColumns || []
  )
  const currentQuery = useAppSelector((state) => state.analysis.querySource?.query)

  useEffect(() => {
    if (
      sourceDataset &&
      sourceColumn &&
      secondSourceDataset &&
      secondSourceColumn &&
      newTableSecondSourceColumns?.length
    ) {
      onQueryChange(
        generateColumnsSourceQuery({
          sourceDataset,
          sourceColumn,
          secondSourceDataset,
          secondSourceColumn,
          newTableSourceColumns,
          newTableSecondSourceColumns
        })
      )
    }
  }, [
    sourceDataset,
    sourceColumn,
    secondSourceDataset,
    secondSourceColumn,
    newTableSourceColumns,
    newTableSecondSourceColumns,
    onQueryChange,
    currentQuery
  ])

  useEffect(() => {
    return () => {
      const settings: ColumnsFromSourceSettings = {
        mainSource: sourceDatasetId,
        mainSourceColumn: sourceColumn,
        secondSource,
        secondSourceColumn,
        newTableSourceColumns,
        newTableSecondSourceColumns,
        analysisType: AnalysisType.AddColumn
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [
    sourceDatasetId,
    sourceColumn,
    secondSource,
    secondSourceColumn,
    newTableSourceColumns,
    newTableSecondSourceColumns,
    onChange,
    dispatch
  ])

  const handleSecondSourceChange = (source) => {
    setSecondSource(source)
    setSecondSourceColumn('')
    setNewTableSecondSourceColumns([])
  }

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.AddColumn}>
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage id={`analysis.${AnalysisType.AddColumn}.title`} />
        </Typography>
      </AnalysisHeader>
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">Data</Typography>
          </Box>
          <BaseSource />
          <Box mt={1}>
            <ColumnsDropdown source={sourceDatasetId} column={sourceColumn} onChange={setSourceColumn} />
          </Box>

          <SecondSourceSelector source={secondSource} showHelpText={false} onChange={handleSecondSourceChange} />
          <SecondSourceColumnSelector
            source={secondSource}
            showLabel={false}
            column={secondSourceColumn}
            margin="none"
            onChange={setSecondSourceColumn}
          />

          <Typography component="div" variant="caption" color="textSecondary" mt={1}>
            <FormattedMessage id="analysis.addColumn.helper" />
          </Typography>
        </AppFormGridColumn>
        <Grid item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id="analysis.addColumn.newColumns" />
            </Typography>
          </Box>

          <MultipleColumnsDropdown
            key="baseSource"
            source={sourceDatasetId}
            columns={newTableSourceColumns}
            selectAll={true}
            labelId={`analysis.${AnalysisType.AddColumn}.baseSource`}
            onChange={setNewTableSourceColumns}
          />

          <MultipleColumnsDropdown
            key="secondSource"
            source={secondSource}
            columns={newTableSecondSourceColumns}
            labelId={`analysis.${AnalysisType.AddColumn}.secondSource`}
            onChange={setNewTableSecondSourceColumns}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default ColumnsFromSourceForm
